import React, { useEffect, useRef, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as S from './AdImagesItem.styles';
import { RemoveRoundIcon, RotateRoundIcon } from '@/components/UI/Icons/Icons';
import { AdImagesItemProps } from '@/components/AdImages/AdImages.types';
import { CommonTestIds } from '@/__e2e__/testIds/common';

export const AdImagesItem: React.FC<AdImagesItemProps> = ({
  image,
  remove,
  onItemClick,
  hasRotationControls,
  isSmall,
  rotate,
}) => {
  const {
    attributes,
    listeners,
    isDragging,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: image.id });
  const [isLoading, setLoading] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);

  const hasLoadingIndicator = image.isUploading || isLoading;

  const style = isDragging
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: 0.7,
        zIndex: 1,
      }
    : {
        transform: CSS.Transform.toString(transform),
      };

  useEffect(() => {
    if (imgRef.current?.complete) {
      setLoading(false);
    }
  }, []);

  const onRotate = (
    event: React.MouseEvent,
    imageId: string,
    angle: number
  ) => {
    event.stopPropagation();
    rotate?.(imageId, angle);
  };

  const onRemove = (event: React.MouseEvent, imageId: string) => {
    event.stopPropagation();
    remove(imageId);
  };

  const stopEvents = {
    onPointerDown: (e: React.SyntheticEvent) => e.stopPropagation(),
    onMouseDown: (e: React.SyntheticEvent) => e.stopPropagation(),
    onTouchStart: (e: React.SyntheticEvent) => e.stopPropagation(),
  };

  return (
    <S.AdImagesItemBox
      ref={setNodeRef}
      style={style}
      data-testid={CommonTestIds.adImages.item}
      {...attributes}
      {...listeners}
      $isSmall={isSmall}
    >
      {hasLoadingIndicator && (
        <S.AdImagesLoadingIndicator
          $filename="uploading.svg"
          $width={48}
          $height={48}
        />
      )}
      {image.src && (
        <S.AdImagesImage
          ref={imgRef}
          src={image.src}
          onLoad={() => setLoading(false)}
          alt=""
          onClick={() => onItemClick?.(image.id)}
          loading="lazy"
        />
      )}
      <S.AdImagesItemBtn
        {...stopEvents}
        $position="tr"
        hasPadding={false}
        appearance="transparent"
        onClick={event => onRemove(event, image.id)}
        $isSmall={isSmall}
      >
        <RemoveRoundIcon isFullOpacity={isSmall} />
      </S.AdImagesItemBtn>

      {hasRotationControls && !image.isUploading && (
        <>
          <S.AdImagesItemBtn
            {...stopEvents}
            $position="br"
            hasPadding={false}
            appearance="transparent"
            onClick={event => onRotate(event, image.id, -90)}
            $isSmall={isSmall}
          >
            <RotateRoundIcon />
          </S.AdImagesItemBtn>

          <S.AdImagesItemBtn
            {...stopEvents}
            $position="bl"
            hasPadding={false}
            appearance="transparent"
            onClick={event => onRotate(event, image.id, 90)}
            $isSmall={isSmall}
            $isMirrored
          >
            <RotateRoundIcon />
          </S.AdImagesItemBtn>
        </>
      )}
    </S.AdImagesItemBox>
  );
};

AdImagesItem.displayName = 'AdImagesItem';
